import { Icon, IconID, IconSize, } from '@thoughtspot/radiant-react/widgets/icon';
import { Horizontal, Vertical, } from '@thoughtspot/radiant-react/widgets/layout';
import { LoadingIndicator } from '@thoughtspot/radiant-react/widgets/loading-indicator';
import { Typography } from '@thoughtspot/radiant-react/widgets/typography';
import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import styles from './spotlight.module.scss';
import { filterSpotlightItems, SpotlightItemType, } from './spotlight.types';
import { CREATE_ACTION_LABELS, DEFAULT_TYPE_LABELS, QUICK_LINKS, RECENT_CONFIG, TYPE_LABEL_CONFIG, } from './spotlight-config';
import { EurekaObjectType, EurekaQueryCompletionType, useSpotlightEurekaCompletions, } from './use-spotlight-completions';
import { useSpotlightShortcut } from './use-spotlight-shortcut';
const EUREKA_OBJECT_PATH_MAP = {
    [EurekaObjectType.ANSWER]: 'saved-answer',
    [EurekaObjectType.PINBOARD]: 'pinboard',
    [EurekaObjectType.VISUALIZATION]: 'pinboard',
};
const EUREKA_OBJECT_ICON_MAP = {
    [EurekaObjectType.ANSWER]: IconID.ANSWER,
    [EurekaObjectType.PINBOARD]: IconID.LIVEBOARD_V2,
    [EurekaObjectType.VISUALIZATION]: IconID.LIVEBOARD_V2,
};
export const Spotlight = ({ onItemSelect, items: customItems, enabled = true, navService, enableEurekaSearch = false, eurekaSearchPath = '/eureka', }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [focusedIndex, setFocusedIndex] = useState(0);
    const [recentItems, setRecentItems] = useState(() => {
        try {
            const stored = sessionStorage.getItem('spotlight-recent-items');
            return stored ? JSON.parse(stored) : [];
        }
        catch {
            return [];
        }
    });
    const searchInputRef = useRef(null);
    const listRef = useRef(null);
    const isKeyboardNavigating = useRef(false);
    useEffect(() => {
        try {
            sessionStorage.setItem('spotlight-recent-items', JSON.stringify(recentItems));
        }
        catch {
        }
    }, [recentItems]);
    const isSearching = !!searchQuery.trim();
    const { objectCompletions, queryCompletions, loading: eurekaLoading, } = useSpotlightEurekaCompletions(searchQuery, enableEurekaSearch && isOpen && isSearching);
    const eurekaObjectItems = useMemo(() => {
        if (!isSearching)
            return [];
        return objectCompletions.map(obj => {
            const urlSegment = EUREKA_OBJECT_PATH_MAP[obj.type] || 'saved-answer';
            const path = obj.type === EurekaObjectType.VISUALIZATION
                ? `/${urlSegment}/${obj.parentGuid}/${obj.guid}`
                : `/${urlSegment}/${obj.guid}/`;
            return {
                id: `eureka-obj-${obj.guid}`,
                label: obj.name,
                description: obj.authorName
                    ? `by ${obj.authorName}`
                    : undefined,
                icon: EUREKA_OBJECT_ICON_MAP[obj.type] || IconID.ANSWER,
                type: SpotlightItemType.EUREKA_OBJECT,
                path,
            };
        });
    }, [isSearching, objectCompletions]);
    const eurekaQueryItems = useMemo(() => {
        if (!isSearching)
            return [];
        return queryCompletions.map(qc => ({
            id: `eureka-query-${qc.query}`,
            label: qc.query,
            description: 'Search in ThoughtSpot',
            icon: qc.type === EurekaQueryCompletionType.RECENT
                ? IconID.CLOCK
                : IconID.SEARCH_V2,
            type: SpotlightItemType.EUREKA_QUERY,
            path: `${eurekaSearchPath}?query=${encodeURIComponent(qc.query)}`,
        }));
    }, [isSearching, queryCompletions, eurekaSearchPath]);
    const quickLinkIds = useMemo(() => new Set(QUICK_LINKS.map(ql => ql.id)), []);
    const allItems = useMemo(() => (customItems ?? []).filter(item => !quickLinkIds.has(item.id)), [customItems, quickLinkIds]);
    const filteredItems = useMemo(() => filterSpotlightItems(allItems, searchQuery), [allItems, searchQuery]);
    const groupedItems = useMemo(() => {
        if (isSearching)
            return null;
        const navigationItems = filteredItems.filter(item => item.type === SpotlightItemType.NAVIGATION);
        const actionItems = filteredItems.filter(item => item.type === SpotlightItemType.ACTION);
        const groups = [];
        if (navigationItems.length > 0) {
            groups.push({
                id: 'navigation',
                title: 'Navigation',
                items: navigationItems,
            });
        }
        if (actionItems.length > 0) {
            groups.push({
                id: 'actions',
                title: 'Quick Actions',
                items: actionItems,
            });
        }
        return groups;
    }, [filteredItems, isSearching]);
    const displayItems = useMemo(() => {
        if (isSearching) {
            return [
                ...eurekaQueryItems,
                ...eurekaObjectItems,
                ...filteredItems,
            ];
        }
        if (!groupedItems)
            return filteredItems;
        return groupedItems.flatMap(group => group.items);
    }, [
        isSearching,
        filteredItems,
        groupedItems,
        eurekaQueryItems,
        eurekaObjectItems,
    ]);
    const orderedCreateActions = useMemo(() => {
        const actionItems = displayItems.filter(item => item.type === SpotlightItemType.ACTION);
        return CREATE_ACTION_LABELS.map(label => actionItems.find(item => item.label === label)).filter(Boolean);
    }, [displayItems]);
    const navigableItems = useMemo(() => {
        if (searchQuery.trim()) {
            return [
                ...eurekaQueryItems,
                ...eurekaObjectItems,
                ...filteredItems,
            ];
        }
        if (displayItems.length === 0) {
            return [];
        }
        const recentDisplayed = recentItems.slice(0, RECENT_CONFIG.displayCount);
        return [
            ...recentDisplayed,
            ...orderedCreateActions,
            ...QUICK_LINKS,
        ];
    }, [
        searchQuery,
        filteredItems,
        eurekaQueryItems,
        eurekaObjectItems,
        displayItems.length,
        recentItems,
        orderedCreateActions,
    ]);
    const openSpotlight = useCallback(() => {
        setIsOpen(true);
        setSearchQuery('');
        setFocusedIndex(0);
    }, []);
    const closeSpotlight = useCallback(() => {
        setIsOpen(false);
        setSearchQuery('');
        setFocusedIndex(0);
    }, []);
    useSpotlightShortcut({
        onOpen: openSpotlight,
        onClose: closeSpotlight,
        isOpen,
        enabled,
    });
    useEffect(() => {
        if (isOpen && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [isOpen]);
    useEffect(() => {
        setFocusedIndex(0);
    }, [navigableItems.length]);
    useEffect(() => {
        if (listRef.current && focusedIndex >= 0) {
            const focusedElement = listRef.current.querySelector(`[data-index="${focusedIndex}"]`);
            if (focusedElement) {
                focusedElement.scrollIntoView({
                    block: 'nearest',
                    behavior: 'smooth',
                });
            }
        }
    }, [focusedIndex]);
    const handleSelectItem = useCallback((item) => {
        if (item.type !== SpotlightItemType.EUREKA_OBJECT &&
            item.type !== SpotlightItemType.EUREKA_QUERY) {
            setRecentItems(prev => {
                const deduped = prev.filter(i => i.id !== item.id);
                return [item, ...deduped].slice(0, RECENT_CONFIG.maxItems);
            });
        }
        closeSpotlight();
        if (onItemSelect) {
            onItemSelect(item);
        }
        else if (item.path && navService?.goToUrl) {
            navService.goToUrl(item.path);
        }
        else if (item.action) {
            item.action();
        }
    }, [closeSpotlight, onItemSelect, navService]);
    const handleKeyDown = useCallback((event) => {
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                isKeyboardNavigating.current = true;
                setFocusedIndex(prev => prev < navigableItems.length - 1 ? prev + 1 : 0);
                break;
            case 'ArrowUp':
                event.preventDefault();
                isKeyboardNavigating.current = true;
                setFocusedIndex(prev => prev > 0 ? prev - 1 : navigableItems.length - 1);
                break;
            case 'Enter':
                event.preventDefault();
                if (navigableItems.length > 0 &&
                    focusedIndex < navigableItems.length) {
                    handleSelectItem(navigableItems[focusedIndex]);
                }
                break;
            case 'Escape':
                event.preventDefault();
                closeSpotlight();
                break;
            default:
                break;
        }
    }, [navigableItems, focusedIndex, handleSelectItem, closeSpotlight]);
    const handleOverlayClick = useCallback((event) => {
        if (event.target === event.currentTarget) {
            closeSpotlight();
        }
    }, [closeSpotlight]);
    const getTypeLabel = (item) => {
        if (item.type === SpotlightItemType.EUREKA_OBJECT) {
            return 'Object';
        }
        if (item.type === SpotlightItemType.EUREKA_QUERY) {
            return 'Search';
        }
        const lowerLabel = item.label.toLowerCase();
        const lowerDesc = item.description?.toLowerCase() || '';
        if (TYPE_LABEL_CONFIG.answerInLiveboard.keywords.some(k => lowerDesc.includes(k) || lowerLabel.includes(k)) &&
            (lowerDesc.includes('lb') || lowerDesc.includes('liveboard'))) {
            return TYPE_LABEL_CONFIG.answerInLiveboard.label;
        }
        const matchedConfig = Object.values(TYPE_LABEL_CONFIG).find(config => config.keywords.some(keyword => lowerDesc.includes(keyword) || lowerLabel.includes(keyword)));
        if (matchedConfig) {
            return matchedConfig.label;
        }
        return item.type === SpotlightItemType.NAVIGATION
            ? DEFAULT_TYPE_LABELS.navigation
            : DEFAULT_TYPE_LABELS.action;
    };
    const renderListItem = (item, index) => {
        const isFocused = index === focusedIndex;
        return (React.createElement("button", { key: item.id, type: "button", className: `${styles.listItem} ${isFocused ? styles.focused : ''}`, onClick: () => handleSelectItem(item), onMouseEnter: () => {
                if (!isKeyboardNavigating.current) {
                    setFocusedIndex(index);
                }
            }, "data-index": index, tabIndex: -1 },
            React.createElement(Horizontal, { vAlignContent: "center", className: styles.itemIcon },
                React.createElement(Icon, { id: item.icon || IconID.SEARCH_V2, size: IconSize.MEDIUM })),
            React.createElement(Vertical, { className: styles.itemContent },
                React.createElement(Typography, { variant: "body-normal", noMargin: true, className: styles.itemLabel }, item.label),
                item.description && (React.createElement(Typography, { variant: "p-small", noMargin: true, className: styles.itemDescription }, item.description))),
            React.createElement(Typography, { variant: "caption", noMargin: true, className: styles.itemType }, getTypeLabel(item))));
    };
    const renderResults = () => {
        if (isSearching) {
            const hasEurekaResults = eurekaQueryItems.length > 0 || eurekaObjectItems.length > 0;
            const hasAppResults = filteredItems.length > 0;
            if (!hasEurekaResults && !hasAppResults && !eurekaLoading) {
                return (React.createElement(Vertical, { hAlignContent: "center", className: styles.emptyState },
                    React.createElement(Icon, { id: IconID.SEARCH_V2, size: IconSize.LARGE, className: styles.emptyIcon }),
                    React.createElement(Typography, { variant: "body-normal", noMargin: true, className: styles.emptyTitle }, "No results found"),
                    React.createElement(Typography, { variant: "p-small", noMargin: true, className: styles.emptyDescription }, "Try a different search term")));
            }
            const results = [];
            let itemIndex = 0;
            if (eurekaQueryItems.length > 0) {
                results.push(React.createElement(Typography, { key: "eureka-query-header", variant: "section-label", noMargin: true, className: styles.categoryTitle }, "Suggested searches"));
                eurekaQueryItems.forEach(item => {
                    results.push(renderListItem(item, itemIndex));
                    itemIndex += 1;
                });
            }
            else if (eurekaLoading) {
                results.push(React.createElement(Typography, { key: "eureka-query-header", variant: "section-label", noMargin: true, className: styles.categoryTitle }, "Suggested searches"), React.createElement(Horizontal, { key: "eureka-query-loading", vAlignContent: "center", className: styles.eurekaLoadingRow },
                    React.createElement(LoadingIndicator.Contextual, { className: styles.eurekaLoadingSpinner })));
            }
            if (eurekaObjectItems.length > 0) {
                results.push(React.createElement(Typography, { key: "eureka-objects-header", variant: "section-label", noMargin: true, className: styles.categoryTitle }, "Objects"));
                eurekaObjectItems.forEach(item => {
                    results.push(renderListItem(item, itemIndex));
                    itemIndex += 1;
                });
            }
            else if (eurekaLoading) {
                results.push(React.createElement(Typography, { key: "eureka-objects-header", variant: "section-label", noMargin: true, className: styles.categoryTitle }, "Objects"), React.createElement(Horizontal, { key: "eureka-objects-loading", vAlignContent: "center", className: styles.eurekaLoadingRow },
                    React.createElement(LoadingIndicator.Contextual, { className: styles.eurekaLoadingSpinner })));
            }
            if (filteredItems.length > 0) {
                results.push(React.createElement(Typography, { key: "app-nav-header", variant: "section-label", noMargin: true, className: styles.categoryTitle }, "App pages"));
                filteredItems.forEach(item => {
                    results.push(renderListItem(item, itemIndex));
                    itemIndex += 1;
                });
            }
            return results;
        }
        if (displayItems.length === 0) {
            return (React.createElement(Vertical, { hAlignContent: "center", className: styles.emptyState },
                React.createElement(Icon, { id: IconID.SEARCH_V2, size: IconSize.LARGE, className: styles.emptyIcon }),
                React.createElement(Typography, { variant: "body-normal", noMargin: true, className: styles.emptyTitle }, "No results found"),
                React.createElement(Typography, { variant: "p-small", noMargin: true, className: styles.emptyDescription }, "Try a different search term")));
        }
        const results = [];
        if (recentItems.length > 0) {
            results.push(React.createElement(Typography, { key: "recent-header", variant: "section-label", noMargin: true, className: styles.categoryTitle }, "Recent"));
            recentItems
                .slice(0, RECENT_CONFIG.displayCount)
                .forEach((item, idx) => {
                results.push(renderListItem(item, idx));
            });
        }
        if (orderedCreateActions.length > 0) {
            const createOffset = Math.min(recentItems.length, RECENT_CONFIG.displayCount);
            results.push(React.createElement(Typography, { key: "actions-header", variant: "section-label", noMargin: true, className: styles.categoryTitle }, "Create new"));
            results.push(React.createElement(Vertical, { key: "create-buttons", className: styles.createNewSection },
                React.createElement(Horizontal, { className: styles.createNewButtons }, orderedCreateActions.map((item, idx) => {
                    const navIndex = createOffset + idx;
                    return (React.createElement("button", { key: item.id, type: "button", className: `${styles.createButton} ${navIndex === focusedIndex
                            ? styles.createButtonFocused
                            : ''}`, onClick: () => handleSelectItem(item), onMouseEnter: () => {
                            if (!isKeyboardNavigating.current) {
                                setFocusedIndex(navIndex);
                            }
                        }, "data-index": navIndex }, item.label.replace('Create ', '')));
                }))));
        }
        if (QUICK_LINKS.length > 0) {
            const quickLinkOffset = Math.min(recentItems.length, RECENT_CONFIG.displayCount) +
                orderedCreateActions.length;
            results.push(React.createElement(Typography, { key: "nav-header", variant: "section-label", noMargin: true, className: styles.categoryTitle }, "Quick links"));
            QUICK_LINKS.forEach((item, idx) => {
                results.push(renderListItem(item, quickLinkOffset + idx));
            });
        }
        return results;
    };
    if (!isOpen) {
        return null;
    }
    return (React.createElement("div", { className: styles.spotlightOverlay, onClick: handleOverlayClick, onKeyDown: handleKeyDown, role: "dialog", "aria-modal": "true", "aria-label": "Spotlight Search" },
        React.createElement(Vertical, { className: styles.spotlightContainer },
            React.createElement("div", { className: styles.searchSection },
                React.createElement(Horizontal, { vAlignContent: "center", className: styles.searchInputWrapper },
                    React.createElement(Icon, { id: IconID.SEARCH_V2, size: IconSize.SMALL, className: styles.searchIcon }),
                    React.createElement("input", { ref: searchInputRef, type: "text", className: styles.searchInput, placeholder: "Search objects or navigate anywhere in Thoughtspot", value: searchQuery, onChange: e => setSearchQuery(e.target.value), autoComplete: "off", autoCorrect: "off", autoCapitalize: "off", spellCheck: false }),
                    eurekaLoading && (React.createElement("div", { className: styles.searchingIndicator, "aria-label": "Searching..." })),
                    !eurekaLoading && (React.createElement(Horizontal, { className: styles.shortcutBadge },
                        React.createElement("kbd", null, "/"),
                        React.createElement("span", null, "to filter"))))),
            React.createElement("div", { ref: listRef, className: styles.resultsSection, onMouseMove: () => {
                    isKeyboardNavigating.current = false;
                } }, renderResults()),
            React.createElement(Horizontal, { vAlignContent: "center", className: styles.footer },
                React.createElement(Horizontal, { className: styles.shortcutHints },
                    React.createElement(Horizontal, { vAlignContent: "center", className: styles.shortcutHint },
                        React.createElement("span", { className: styles.shortcutKey }, "\u2191 \u2193"),
                        "Navigate"),
                    React.createElement(Horizontal, { vAlignContent: "center", className: styles.shortcutHint },
                        React.createElement("span", { className: styles.shortcutKey }, "Shift"),
                        React.createElement("span", { className: styles.shortcutKey }, "+"),
                        React.createElement("span", { className: styles.shortcutKey }, "\u21B5"),
                        "Open in new tab"),
                    React.createElement(Horizontal, { vAlignContent: "center", className: styles.shortcutHint },
                        React.createElement("span", { className: styles.shortcutKey }, "\u2318"),
                        React.createElement("span", { className: styles.shortcutKey }, "+"),
                        React.createElement("span", { className: styles.shortcutKey }, "\u21B5"),
                        "Open in Spotter"),
                    React.createElement(Horizontal, { vAlignContent: "center", className: styles.shortcutHint },
                        React.createElement("span", { className: styles.shortcutKey }, "\u21B5"),
                        "Open"))))));
};
export default Spotlight;
//# sourceMappingURL=spotlight.js.map